import pluralize from "pluralize";
import { useContext, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { POCContext } from "../../contexts/POCContext";
import useAPI from "./useAPI";

export const usePOC = (handleDrawerDisplay = null, resetSelection = null) => {
  const location = useLocation();
  const [viewedControls, setViewedControls] = useState(null);
  const [remainingControlsCount, setRemainingControlsCount] = useState(0);
  const [pocDialogOpen, setPocDialogOpen] = useState(false);
  const [dialogMessageFirstLine, setDialogMessageFirstLine] = useState("");
  const [dialogMessageSecondLine, setDialogMessageSecondLine] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [controlLoading, setControlLoading] = useState(false);
  const [pocLimits, setPocLimits] = useState(null);
  const [pocDataLoading, setPocDataLoading] = useState(true);

  const {
    trackEvent,
    checkControlIsAllowed,
    markControlAsViewed,
    getControlViewedToday,
    getRemainingControlsLimitToday,
    getPOCLimits,
  } = useAPI();
  const { isPOC } = useContext(POCContext);

  useEffect(() => {
    if (isPOC) {
      setPocDataLoading(true);
      Promise.all([
        getControlViewedToday().then((response) => {
          setViewedControls(response.data);
        }),
        getRemainingControlsLimitToday().then((response) => {
          setRemainingControlsCount(response.data);
        }),
        getPOCLimits().then((response) => {
          setPocLimits(response.data);
        }),
      ]).then(() => {
        setPocDataLoading(false);
      });
    }
  }, [isPOC]);

  const decideLimitReached = () => {
    if (pocLimits === null) {
      getPOCLimits().then((response) => {
        setPocLimits(response.data);
        decideLimitReachedTexting(response.data);
      });
    } else {
      decideLimitReachedTexting(pocLimits);
    }
  };

  const decideLimitReachedTexting = (limits) => {
    if (limits.total_controls_limit - limits.total_controls_viewed === 0) {
      setDialogTitle("POC Limit Reached");
      setDialogMessageFirstLine(
        "You have reached the maximum number of views allowed during the POC period."
      );
      setDialogMessageSecondLine("Please contact us for further assistance.");
    } else {
      setDialogTitle("Limit Reached");
      setDialogMessageFirstLine(
        "You have reached your limit of views for today."
      );
      setDialogMessageSecondLine("Please try again tomorrow.");
    }
  };

  const handlePOCClick = (controlId) => {
    if (location.pathname.includes("remediation")) {
      setSearchParams({ remediation: controlId });
    } else if (location.pathname.includes("controls")) {
      setSearchParams({ control_id: controlId });
    }
  };

  const handleControlNotViewed = () => {
    if (remainingControlsCount === 0) {
      decideLimitReached();
    } else {
      setDialogTitle("Confirmation");
      setDialogMessageFirstLine(
        `You have ${remainingControlsCount}  ${pluralize(
          "view",
          remainingControlsCount
        )}  left for today.`
      );
      setDialogMessageSecondLine("Are you sure you want to continue?");
    }
    setPocDialogOpen(true);
  };

  const isControlViewedToday = (controlId) => {
    if (viewedControls) {
      return viewedControls.includes(controlId.toString());
    }
    return false;
  };

  const handleDialogConfirmation = (controlId) => {
    setControlLoading(true);
    checkControlIsAllowed(controlId).then((response) => {
      if (response.data) {
        markControlAsViewed(controlId).then(() => {
          getControlViewedToday().then((response) => {
            setViewedControls(response.data);
          });
          getRemainingControlsLimitToday().then((response) => {
            setRemainingControlsCount(response.data);
          });
          setControlLoading(false);
          handleDrawerDisplay(true);
        });
      }
    });
  };

  const handleDialogClose = (confirmed, controlId = null) => {
    setPocDialogOpen(false);
    if (confirmed && remainingControlsCount > 0) {
      trackEvent("user click on 'Yes' to view control in POC mode");
      handleDialogConfirmation(controlId);
    }
    if (!confirmed) {
      trackEvent("user click on 'No' to view control in POC mode");
      resetSelection();
    }
  };

  const handlePOCDrawer = (controlId) => {
    checkControlIsAllowed(controlId).then((response) => {
      if (response.data) {
        if (isControlViewedToday(controlId)) {
          handleDrawerDisplay(true);
        } else {
          handleControlNotViewed();
        }
        // markControlAsViewed(controlId).then();
      } else {
        handleDrawerDisplay(false);

        decideLimitReached();
        setPocDialogOpen(true);
        let trackMessage = `User tried to open drawer for ${controlId} but is not allowed - POC`;
        trackEvent(trackMessage);
      }
    });
  };

  return {
    viewedControls,
    pocDataLoading,
    remainingControlsCount,
    pocDialogOpen,
    dialogMessageFirstLine,
    dialogMessageSecondLine,
    dialogTitle,
    controlLoading,
    setViewedControls,
    setRemainingControlsCount,
    setPocDialogOpen,
    setDialogMessageFirstLine,
    setDialogMessageSecondLine,
    setDialogTitle,
    isControlViewedToday,
    handleDialogClose,
    handlePOCClick,
    handlePOCDrawer,
  };
};
